import {
  Box,
  Checkbox,
  Container,
  HStack,
  Heading,
  Input,
  Progress,
  Stack,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import * as React from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { connectIntegration } from "../api/integration";
import {
  completeOnboarding,
  requestSlackInvite,
  saveOnboardingProgress,
} from "../api/onboarding";
import AppShell from "../components/app-shell";
import { Button } from "../components/button";
import { Field } from "../components/field";
import { Logo } from "../components/logo";
import { Toaster, toaster } from "../components/toaster";
import { useAppDispatch, useAppSelector, useLoading } from "../hooks";
import { brand } from "../lib/brand";
import {
  AdPlatform,
  ONBOARDING_STEPS,
  OnboardingAnswers,
  OnboardingStepId,
  visibleSteps,
} from "../lib/onboarding";
import { checkUser } from "../store/authSlice";
import {
  loadIntegrations,
  selectCurrentWorkspace,
} from "../store/workspaceSlice";
import { IntegrationStatus } from "../types/integration";

const isOnboardingStepId = (s: unknown): s is OnboardingStepId =>
  typeof s === "string" && ONBOARDING_STEPS.some((step) => step.id === s);

export default function OnboardingPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceSlug = workspace.slug;
  const serverStep = workspace["onboarding-current-step"];
  const serverAnswers = workspace["onboarding-answers"];
  const isComplete = workspace["onboarding-complete?"];

  const [answers, setAnswers] = React.useState<OnboardingAnswers>(() => ({
    email: user?.username,
    company: workspace.name,
    ...((serverAnswers as OnboardingAnswers | undefined) ?? {}),
  }));
  const [stepId, setStepId] = React.useState<OnboardingStepId>(
    isOnboardingStepId(serverStep) ? serverStep : "profile",
  );
  const [isSaving, setIsSaving] = React.useState(false);

  if (brand.id !== "addie") {
    return <Navigate to="/" replace />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (isComplete) {
    return <Navigate to={`/workspaces/${workspaceSlug}`} replace />;
  }

  const steps = visibleSteps(answers);
  const stepIdx = Math.max(
    0,
    steps.findIndex((s) => s.id === stepId),
  );
  const step = steps[stepIdx];
  const isFirst = stepIdx === 0;
  const isFinal = step.id === "done";

  const updateAnswers = (patch: OnboardingAnswers) =>
    setAnswers((prev) => ({ ...prev, ...patch }));

  const advance = async () => {
    const next = steps[Math.min(stepIdx + 1, steps.length - 1)];
    setIsSaving(true);
    try {
      await saveOnboardingProgress(workspaceSlug, next.id, answers);
      setStepId(next.id);
    } catch {
      toaster.create({ title: "Could not save progress.", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const back = () => {
    const prev = steps[Math.max(stepIdx - 1, 0)];
    setStepId(prev.id);
  };

  const finish = async () => {
    setIsSaving(true);
    try {
      const { redirect_url } = await completeOnboarding(workspaceSlug);
      await dispatch(checkUser());
      navigate(redirect_url);
    } catch {
      toaster.create({ title: "Could not finish onboarding.", type: "error" });
      setIsSaving(false);
    }
  };

  return (
    <AppShell bg="navy">
      <Container maxW="2xl" py={{ base: "12", md: "20" }}>
        <Stack gap="8">
          <HStack justify="center">
            <Logo height="6" />
          </HStack>
          <Progress.Root
            value={((stepIdx + 1) / steps.length) * 100}
            colorPalette="blue"
          >
            <Progress.Track>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
          <Box
            bg="bg"
            borderRadius="xl"
            p={{ base: "6", md: "10" }}
            shadow="md"
          >
            <Stack gap="6">
              <Stack gap="1">
                <Text fontSize="sm" color="fg.muted">
                  Step {stepIdx + 1} of {steps.length}
                </Text>
                <Heading size="lg" color="fg.accent">
                  {step.title}
                </Heading>
              </Stack>
              <StepBody
                step={step.id}
                workspaceSlug={workspaceSlug}
                answers={answers}
                onChange={updateAnswers}
              />
              <HStack justify="space-between" pt="4">
                {!isFirst ? (
                  <Button variant="ghost" onClick={back} disabled={isSaving}>
                    Back
                  </Button>
                ) : (
                  <Box />
                )}
                <HStack>
                  {step.skippable && !isFinal && (
                    <Button
                      variant="ghost"
                      onClick={advance}
                      disabled={isSaving}
                    >
                      Skip
                    </Button>
                  )}
                  {isFinal ? (
                    <Button variant="solid" onClick={finish} loading={isSaving}>
                      Go to dashboard
                    </Button>
                  ) : (
                    <Button
                      variant="solid"
                      onClick={advance}
                      disabled={!canAdvance(step.id, answers) || isSaving}
                      loading={isSaving}
                    >
                      Continue
                    </Button>
                  )}
                </HStack>
              </HStack>
            </Stack>
          </Box>
        </Stack>
      </Container>
      <Toaster />
    </AppShell>
  );
}

const canAdvance = (
  step: OnboardingStepId,
  answers: OnboardingAnswers,
): boolean => {
  switch (step) {
    case "profile":
      return ["first-name", "last-name", "email", "company", "title"].every(
        (k) =>
          Boolean(answers[k as keyof OnboardingAnswers]?.toString().trim()),
      );
    case "platforms":
      return (answers.platforms ?? []).length > 0;
    case "connect-linkedin":
      return Boolean(answers["linkedin-connected"]);
    case "connect-google":
      return Boolean(answers["google-ads-connected"]);
    case "connect-meta":
      return Boolean(answers["meta-connected"]);
    case "book-google-call":
    case "slack":
    case "done":
      return true;
  }
};

interface AnswerStepProps {
  answers: OnboardingAnswers;
  onChange: (patch: OnboardingAnswers) => void;
}

interface StepBodyProps extends AnswerStepProps {
  step: OnboardingStepId;
  workspaceSlug: string;
}

const StepBody = ({
  step,
  workspaceSlug,
  answers,
  onChange,
}: StepBodyProps) => {
  switch (step) {
    case "profile":
      return <ProfileStep answers={answers} onChange={onChange} />;
    case "platforms":
      return <PlatformsStep answers={answers} onChange={onChange} />;
    case "connect-linkedin":
      return (
        <ConnectLinkedInStep
          workspaceSlug={workspaceSlug}
          answers={answers}
          onChange={onChange}
        />
      );
    case "connect-google":
      return (
        <ConnectGoogleStep
          workspaceSlug={workspaceSlug}
          answers={answers}
          onChange={onChange}
        />
      );
    case "book-google-call":
      return <BookGoogleCallStep />;
    case "connect-meta":
      return (
        <ConnectMetaStep
          workspaceSlug={workspaceSlug}
          answers={answers}
          onChange={onChange}
        />
      );
    case "slack":
      return (
        <SlackStep
          workspaceSlug={workspaceSlug}
          answers={answers}
          onChange={onChange}
        />
      );
    case "done":
      return <DoneStep />;
  }
};

const ProfileStep = ({ answers, onChange }: AnswerStepProps) => (
  <Stack gap="4">
    <HStack gap="4" align="start">
      <Field label="First name">
        <Input
          value={answers["first-name"] ?? ""}
          onChange={(e) => onChange({ "first-name": e.target.value })}
          placeholder="First name"
        />
      </Field>
      <Field label="Last name">
        <Input
          value={answers["last-name"] ?? ""}
          onChange={(e) => onChange({ "last-name": e.target.value })}
          placeholder="Last name"
        />
      </Field>
    </HStack>
    <Field label="Business email">
      <Input
        type="email"
        value={answers.email ?? ""}
        onChange={(e) => onChange({ email: e.target.value })}
        placeholder="Business email"
      />
    </Field>
    <Field label="Business name">
      <Input
        value={answers.company ?? ""}
        onChange={(e) => onChange({ company: e.target.value })}
        placeholder="Business name"
      />
    </Field>
    <Field label="Job title">
      <Input
        value={answers.title ?? ""}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="Job title"
      />
    </Field>
  </Stack>
);

const PlatformsStep = ({ answers, onChange }: AnswerStepProps) => {
  const platforms = new Set(answers.platforms ?? []);
  const toggle = (p: AdPlatform) => {
    const next = new Set(platforms);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    onChange({ platforms: Array.from(next) });
  };
  return (
    <Stack gap="3">
      <Text color="fg.muted" fontSize="sm">
        Select every platform you advertise on today. You can connect them in
        the next steps.
      </Text>
      <PlatformChoice
        label="LinkedIn Ads"
        description="Connect via OAuth in the next step."
        checked={platforms.has("linkedin")}
        onToggle={() => toggle("linkedin")}
      />
      <PlatformChoice
        label="Google Ads"
        description="Talk to our team to enable Google support."
        checked={platforms.has("google")}
        onToggle={() => toggle("google")}
      />
      <PlatformChoice
        label="Meta"
        description="Connect via OAuth in the next step."
        checked={platforms.has("meta")}
        onToggle={() => toggle("meta")}
      />
    </Stack>
  );
};

const PlatformChoice = ({
  label,
  description,
  checked,
  onToggle,
}: {
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}) => (
  <Checkbox.Root
    checked={checked}
    onCheckedChange={onToggle}
    css={{
      border: "1px solid",
      borderColor: checked ? "accent" : "border",
      borderRadius: "md",
      padding: "4",
    }}
  >
    <Checkbox.HiddenInput />
    <Checkbox.Control />
    <Stack gap="0">
      <Checkbox.Label fontWeight="semibold">{label}</Checkbox.Label>
      <Text fontSize="sm" color="fg.muted">
        {description}
      </Text>
    </Stack>
  </Checkbox.Root>
);

interface ConnectIntegrationStepProps extends AnswerStepProps {
  workspaceSlug: string;
  integrationId: "linkedin" | "google-ads" | "facebook";
  answerKey: "linkedin-connected" | "google-ads-connected" | "meta-connected";
  label: string;
  description: string;
}

const ConnectIntegrationStep = ({
  workspaceSlug,
  answers,
  onChange,
  integrationId,
  answerKey,
  label,
  description,
}: ConnectIntegrationStepProps) => {
  const dispatch = useAppDispatch();
  const integrations = useAppSelector((s) => s.workspace.integrations);
  const connected =
    integrations.find((i) => i.id === integrationId)?.status ===
    IntegrationStatus.connected;
  const [isConnecting, setIsConnecting] = React.useState(false);

  React.useEffect(() => {
    dispatch(loadIntegrations());
    const onFocus = () => {
      dispatch(loadIntegrations());
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch]);

  React.useEffect(() => {
    if (connected && !answers[answerKey]) {
      onChange({ [answerKey]: true });
    }
  }, [connected, answerKey, answers, onChange]);

  const connect = async () => {
    setIsConnecting(true);
    try {
      const { "oauth-url": oauthURL } = await connectIntegration(
        workspaceSlug,
        integrationId,
        {},
      );
      if (oauthURL) {
        document.location.href = oauthURL;
      } else {
        dispatch(loadIntegrations());
        setIsConnecting(false);
      }
    } catch {
      toaster.create({
        title: `Could not start ${label} connect.`,
        type: "error",
      });
      setIsConnecting(false);
    }
  };

  return (
    <Stack gap="4">
      <Text color="fg.muted" fontSize="sm">
        {description}
      </Text>
      <Button
        variant="solid"
        onClick={connect}
        loading={isConnecting}
        disabled={connected || isConnecting}
      >
        {connected ? "Connected" : `Connect ${label}`}
      </Button>
      <Text fontSize="sm" color="fg.muted">
        {connected
          ? `${label} is connected. You can continue.`
          : `We'll redirect you to ${label} to authorize. You'll come back here when done.`}
      </Text>
    </Stack>
  );
};

const ConnectLinkedInStep = (
  props: AnswerStepProps & { workspaceSlug: string },
) => (
  <ConnectIntegrationStep
    {...props}
    integrationId="linkedin"
    answerKey="linkedin-connected"
    label="LinkedIn Ads"
    description={`Connect your LinkedIn Ads account so ${brand.productName} can manage campaigns on your behalf.`}
  />
);

const ConnectGoogleStep = (
  props: AnswerStepProps & { workspaceSlug: string },
) => (
  <ConnectIntegrationStep
    {...props}
    integrationId="google-ads"
    answerKey="google-ads-connected"
    label="Google Ads"
    description={`Connect your Google Ads account so ${brand.productName} can read your spend and densify signal.`}
  />
);

const ConnectMetaStep = (
  props: AnswerStepProps & { workspaceSlug: string },
) => (
  <ConnectIntegrationStep
    {...props}
    integrationId="facebook"
    answerKey="meta-connected"
    label="Meta"
    description={`Connect your Meta ad account so ${brand.productName} can send valued conversions back to it.`}
  />
);

const BookGoogleCallStep = () => (
  <Stack gap="4">
    <Text color="fg.muted" fontSize="sm">
      Google Ads support requires a brief call with our team to get you set up.
      Book a slot below — you can continue while you wait for the meeting.
    </Text>
    <Button variant="outline" asChild>
      <a
        href="https://cal.com/addiehq/google"
        target="_blank"
        rel="noopener noreferrer"
      >
        Book a call
      </a>
    </Button>
  </Stack>
);

const SlackStep = ({
  workspaceSlug,
  answers,
  onChange,
}: AnswerStepProps & { workspaceSlug: string }) => {
  const [inviteURL, setInviteURL] = React.useState<string | null>(null);
  const [isLoading, withLoading] = useLoading(true);

  React.useEffect(() => {
    const controller = new AbortController();
    withLoading(async () => {
      try {
        const { url } = await requestSlackInvite(
          workspaceSlug,
          controller.signal,
        );
        setInviteURL(url);
      } catch (e) {
        // The abort on unmount rejects the request; ignore it (the
        // component is gone) and only surface real failures.
        if (!axios.isCancel(e)) {
          setInviteURL(null);
        }
      }
    });
    return () => controller.abort();
  }, [withLoading, workspaceSlug]);

  return (
    <Stack gap="4">
      <Text color="fg.muted" fontSize="sm">
        Join a shared Slack channel with our team for direct support and product
        updates. This step is optional — you can do it later from settings.
      </Text>
      {isLoading ? (
        <Button variant="outline" loading disabled>
          Join Slack
        </Button>
      ) : inviteURL ? (
        <Button
          variant="outline"
          onClick={() => {
            window.open(inviteURL, "_blank", "noopener");
            onChange({ "slack-connected": true });
          }}
        >
          Join Slack
        </Button>
      ) : (
        <Text fontSize="sm" color="fg.muted">
          Slack is unavailable right now — you can skip this step and join later
          from settings.
        </Text>
      )}
      {answers["slack-connected"] && (
        <Text fontSize="sm" color="fg.muted">
          Thanks! Check your email to accept the Slack Connect invite.
        </Text>
      )}
    </Stack>
  );
};

const DoneStep = () => (
  <Stack gap="3">
    <Text>
      {brand.productName} is ready to go. Click below to head to your dashboard.
    </Text>
    <Text fontSize="sm" color="fg.muted">
      Need help? Email {brand.supportEmail}.
    </Text>
  </Stack>
);
